/* The Virtus Project — cart drawer (theme build).
   Same UI as the static site's cart.js, backed by Shopify's AJAX cart:
   GET /cart.js to render, POST /cart/add.js to add, POST /cart/change.js for quantities.
   The £12 notebook price relies on the automatic Buy-X-Get-Y discount (Lockbox → Notebook 20% off);
   line-level discount allocations from /cart.js drive the struck-through prices. */
(() => {
  const routes = window.routes || {};
  const ADD_URL = (routes.cart_add_url || '/cart/add') + '.js';
  const CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  const CART_URL = (routes.cart_url || '/cart') + '.js';

  const V = {
    LOCKBOX_1: 49287583432985,
    LOCKBOX_2: 58401393672579,
    LOCKBOX_3: 58401396228483,
    NOTEBOOK_1: 48925081501977,
    NOTEBOOK_2: 58439401570691,
    NOTEBOOK_3: 58439403241859
  };
  const NOTEBOOK_IDS = [V.NOTEBOOK_1, V.NOTEBOOK_2, V.NOTEBOOK_3];

  let cart = null; // last /cart.js payload
  let busy = false;

  const money = pence => {
    const v = pence / 100;
    return '£' + (Number.isInteger(v) ? v : v.toFixed(2));
  };

  const bumpEligible = () => {
    if (!cart) return false;
    const ids = cart.items.map(i => i.variant_id);
    return (ids.includes(V.LOCKBOX_1) || ids.includes(V.LOCKBOX_2) || ids.includes(V.LOCKBOX_3)) &&
      !ids.some(id => NOTEBOOK_IDS.includes(id));
  };

  // ── drawer markup (custom .cart-* classes only) ──
  const root = document.createElement('div');
  root.className = 'cart-root';
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = `
    <div class="cart-backdrop" data-cart-close></div>
    <aside class="cart-panel" role="dialog" aria-modal="true" aria-label="Cart">
      <div class="cart-head">
        <span class="cart-title">Your Cart<small data-cart-head-count></small></span>
        <button type="button" class="cart-x" aria-label="Close cart" data-cart-close>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>
        </button>
      </div>
      <div class="cart-items" data-cart-items></div>
      <div class="cart-foot" data-cart-foot>
        <div class="cart-sub"><span class="lbl">Subtotal</span><span class="amt" data-cart-subtotal>£0</span></div>
        <p class="cart-note">Free UK shipping · taxes included.</p>
        <a href="/checkout" class="btn btn-primary w-full cart-checkout" data-cart-checkout>Checkout</a>
        <p class="cart-trust">365-day money-back guarantee</p>
      </div>
    </aside>`;

  const q = s => root.querySelector(s);
  let lastFocus = null;
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const assets = window.tvpCartAssets || {};

  const bumpHTML = () => `
    <div class="cart-bump-wrap">
      <span class="cart-bump-label">Complete your detox</span>
      <button type="button" class="bump" data-bump-add>
        <span class="bump-plus" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></span>
        <img src="${esc(assets.bumpImg || '')}" alt="The Notebook v1.0, open with a gel pen" width="240" height="240" loading="lazy" decoding="async" />
        <span class="bump-t">
          <span class="t1">Add The Notebook</span>
          <span class="t2"><b>Save&nbsp;£3</b> with your Lockbox</span>
        </span>
        <span class="bump-p">
          <span class="was">£15</span>
          <span class="now">£12</span>
        </span>
      </button>
    </div>`;

  const itemHTML = it => {
    const discounted = it.final_line_price < it.original_line_price;
    let sub = '';
    if (discounted) sub = money(it.original_line_price - it.final_line_price) + ' bundle saving applied';
    return `
    <div class="cart-item" data-key="${esc(it.key)}">
      <img src="${esc(it.image || '')}" alt="${esc(it.product_title)}" width="120" height="120" loading="lazy" decoding="async" />
      <div class="cart-it">
        <span class="t1">${esc(it.product_title)}</span>
        ${sub ? `<span class="t2">${esc(sub)}</span>` : ''}
        <span class="cart-step">
          <button type="button" data-qty-minus aria-label="Decrease quantity">−</button>
          <span>${it.quantity}</span>
          <button type="button" data-qty-plus aria-label="Increase quantity">+</button>
        </span>
      </div>
      <div class="cart-pr">
        ${discounted ? `<span class="was">${money(it.original_line_price)}</span>` : ''}
        <span class="now">${money(it.final_line_price)}</span>
      </div>
    </div>`;
  };

  const render = () => {
    if (!cart) return;
    const count = cart.item_count;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    q('[data-cart-head-count]').textContent = count ? (count === 1 ? '1 item' : count + ' items') : '';

    const wrap = q('[data-cart-items]');
    if (!cart.items.length) {
      wrap.innerHTML = `
        <div class="cart-empty">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 8.5h13l-1.2 11a1.6 1.6 0 0 1-1.6 1.5H8.3a1.6 1.6 0 0 1-1.6-1.5z"/><path d="M8.6 8.5V7a3.4 3.4 0 0 1 6.8 0v1.5"/></svg>
          <p>Your cart is empty.</p>
          <a href="${esc(assets.productUrl || '/products/the-lockbox')}" class="btn btn-ghost cart-empty-cta">Shop The Lockbox</a>
        </div>`;
      q('[data-cart-foot]').style.display = 'none';
      return;
    }
    q('[data-cart-foot]').style.display = '';
    wrap.innerHTML = cart.items.map(itemHTML).join('') + (bumpEligible() ? bumpHTML() : '');
    q('[data-cart-subtotal]').textContent = money(cart.total_price);
  };

  const refresh = async () => {
    const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    cart = await res.json();
    render();
  };

  const open = () => {
    lastFocus = document.activeElement;
    render();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
    q('.cart-x').focus();
    refresh().catch(() => {});
  };
  const close = () => {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open');
    lastFocus?.focus?.();
  };

  const add = async (variantId, qty = 1) => {
    if (busy || !variantId) return;
    busy = true;
    try {
      const res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
      });
      if (!res.ok) throw new Error('add failed');
      await refresh();
      if (!root.classList.contains('is-open')) open();
    } catch (err) {
      alert('Sorry — something went wrong adding to cart. Please try again.');
    } finally {
      busy = false;
    }
  };

  const changeQty = async (key, qty) => {
    if (busy) return;
    busy = true;
    try {
      const res = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      if (!res.ok) throw new Error('change failed');
      cart = await res.json();
      render();
    } catch (err) {
      refresh().catch(() => {});
    } finally {
      busy = false;
    }
  };

  // ── events ──
  root.addEventListener('click', e => {
    if (e.target.closest('[data-cart-close]')) return close();
    if (e.target.closest('[data-bump-add]')) return add(V.NOTEBOOK_1);
    const row = e.target.closest('.cart-item');
    if (!row || !cart) return;
    const it = cart.items.find(i => i.key === row.dataset.key);
    if (!it) return;
    if (e.target.closest('[data-qty-plus]')) changeQty(it.key, it.quantity + 1);
    if (e.target.closest('[data-qty-minus]')) changeQty(it.key, it.quantity - 1);
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && root.classList.contains('is-open')) close();
  });
  // header cart button + single-product add buttons (offer cards, emergencies CTA)
  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) { e.preventDefault(); open(); return; }
    const single = e.target.closest('[data-add-single]');
    if (single) {
      e.preventDefault();
      add(single.getAttribute('data-variant-id') || V.LOCKBOX_1);
    }
  });

  const mount = () => {
    document.body.appendChild(root);
    refresh().catch(() => {});
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', mount) : mount();

  window.TVPCart = { add, open, close, refresh };
})();
